import { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { ConversationList, type ConversationListRef } from '@/components/conversation-list';
import { MessageView } from '@/components/message-view';
import { AuthGuard } from '@/components/auth-guard';
import { AppLayout } from '@/components/app-layout';
import { PageFallback } from '@/components/page-fallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { brandClient } from '@/lib/brand-client';
import { conversationClient } from '@/lib/gupshup-client';
import { posProvidersClient } from '@/lib/pos-providers-client';
import { couponsSurfaceEnabled } from '@/lib/discounts-client';
import { getStoredSession } from '@/lib/auth-client';
import { masterLineOf } from '@/lib/session-scope';
import { aggregateShowsWhatsappChrome, hasLine, homePath, showInbox, showWhatsappChrome, takesOrders } from '@/lib/commerce-mode';
import { getSessionMode } from '@/components/brand-management/settingsAccess';
import {
    defaultSettingsSection,
    visibleSettingsSections,
    type SectionVisibility,
} from '@/components/brand-management/settings-sections';
import { BrandFilterProvider, useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import { AggregateConfigBanner } from '@/components/aggregate-config-banner';
import { NoSurfaceEmptyState } from '@/components/no-surface-empty-state';
import { useBrandStatus } from '@/hooks/use-brand-status';
import { usePendingOrdersCount } from '@/hooks/use-pending-orders-count';
import { useHandoffCount } from '@/hooks/use-handoff-count';
import { useNotificationStream } from '@/hooks/use-notification-stream';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConversationDraft } from '@/hooks/use-conversation-draft';
import { CartPanel } from '@/components/cart/cart-panel';
import { CartStartPanel } from '@/components/cart/cart-start-panel';
import type { ViewConversationOptions } from '@/components/orders/order-detail-panel';
import { ConfirmSummaryDialog } from '@/components/cart/confirm-summary-dialog';
import type { ConfirmDraftResult } from '@/lib/draft-order-client';
import type { BrandIdentityDto, LocationResponseDto } from '@/types/brand';
import type { SessionScope } from '@/types/auth';

// The conversation view ("/conversations") stays eager — it's the surface every
// notification and every card action lands on. The rest of the routes load on
// demand; /settings alone pulls in the 80+ file brand-management tree (plus
// leaflet via its locations tab).
const BrandManagementPage = lazy(() =>
    import('@/components/brand-management/BrandManagementPage').then((m) => ({
        default: m.BrandManagementPage,
    })),
);
const OrdersBoard = lazy(() =>
    import('@/components/orders/board/orders-board').then((m) => ({ default: m.OrdersBoard })),
);
const OrdersTab = lazy(() =>
    import('@/components/orders/orders-tab').then((m) => ({ default: m.OrdersTab })),
);
const BankTransfersTab = lazy(() =>
    import('@/components/bank-transfers/bank-transfers-tab').then((m) => ({ default: m.BankTransfersTab })),
);
const TemplateManagementPage = lazy(() =>
    import('@/components/templates/TemplateManagementPage').then((m) => ({
        default: m.TemplateManagementPage,
    })),
);
const DiscountsPage = lazy(() =>
    import('@/components/discounts/discounts-page').then((m) => ({ default: m.DiscountsPage })),
);
const PlaygroundPage = lazy(() =>
    import('@/components/playground/playground-page').then((m) => ({ default: m.PlaygroundPage })),
);
const ResultsPage = lazy(() => import('@/components/results/results-page').then((m) => ({ default: m.ResultsPage })));

type Conversation = {
    id: string;
    phoneNumber: string;
    contactName?: string;
    aiDisabledUntil?: string;
    handoffUntil?: string;
    /** A DRAFT order is open on this conversation (from the list poll). Drives the cart poll cadence. */
    hasActiveOrder?: boolean;
    /** Aggregate sessions only: which brand/branch this chat belongs to (labels + line resolution). */
    brandId?: string;
    brandName?: string;
    locationName?: string | null;
    /** Arrived on an unlinked sucursal line — the agent does not answer there. */
    lineUnlinked?: boolean;
};

export default function App() {
    return (
        <AuthGuard>
            <BrandFilterProvider>
                <AuthenticatedApp />
                <Toaster />
            </BrandFilterProvider>
        </AuthGuard>
    );
}

/**
 * Location-scoped sessions (client-level access) see a single-location view:
 * data lists are filtered server-side; here we pin the locations list and show
 * the right config notice per session shape (writes are 403'd anyway).
 *
 * - Shared-line sucursales (no own line): everything is master-managed.
 * - Own-line sucursales: config is inherited but per-field overridable.
 *
 * Banner texts live in the 'app' namespace: scopeBanner.readOnly / scopeBanner.overridable.
 */
function ScopeBanner({ message }: { message: string }) {
    return (
        <div className="border-b border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-4 py-2 text-sm text-blue-800 dark:text-blue-300 shrink-0">
            {message}
        </div>
    );
}

/** How long "no me recuerdes para este chat" keeps the AI-resume offer quiet. */
const AI_RESUME_SNOOZE_MS = 60 * 60 * 1000;

function AuthenticatedApp() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    useNotificationStream();
    const storedSession = getStoredSession();
    const sessionScope = storedSession?.scope ?? null;
    /**
     * What this session may do (K5). Everything adaptive below — the home route, the WhatsApp
     * chrome, the settings sections — reads from here rather than from the WhatsApp line, so a
     * web-only tenant gets a complete app and a line-less one is not a broken one.
     */
    const sessionAccess = storedSession?.access ?? null;
    const whatsappLine = hasLine(sessionAccess);
    const { isAggregate, selectedBrands, activeBrand, brandById, includeBrand } = useBrandFilter();
    /**
     * An aggregate session has no `access` block (it is null for a parent master client), so its
     * WhatsApp chrome comes from the child brands in the filter: as long as one of them sells over
     * WhatsApp the inbox exists, with the brand/branch chips telling the rows apart.
     */
    const whatsappChrome = isAggregate
        ? aggregateShowsWhatsappChrome(selectedBrands)
        : showWhatsappChrome(sessionAccess);
    /**
     * The inbox, the handoff badge and every "open the chat" link from orders and transfers. The
     * WhatsApp chrome, minus a sucursal session whose brand hides its chats from locations
     * (`access.conversationsHidden`, display-only). An aggregate session is a master: always shown.
     */
    const inboxVisible = isAggregate ? whatsappChrome : showInbox(sessionAccess);
    const [selectedConversation, setSelectedConversation] = useState<Conversation>();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    /**
     * A conversation whose cart should open once it is the selected one — an unpaid order switched
     * from the orders surfaces came back as a draft there. Deferred rather than set directly because
     * selecting a conversation closes the cart (the effect below).
     */
    const [openCartFor, setOpenCartFor] = useState<string | null>(null);
    // Held here rather than inside the panel because it has to pause the draft polling, which lives
    // one level up: a refetch landing mid-edit would either clobber the working copy or make the
    // "unsaved changes" banner compare against the wrong baseline.
    const [cartDirty, setCartDirty] = useState(false);
    /**
     * An order the operator just placed whose customer has not been told about it yet.
     *
     * Owned here, not in the cart panel: confirming ends the draft, so the panel unmounts on the very
     * next poll — and with it would go the only copy of the tracking URL and the payment link, which
     * exist nowhere else in the UI. Kept until a message actually goes out (or the operator dismisses
     * it), including across closing the panel and switching conversations, because an order sitting in
     * the POS with a ticking payment link and a silent customer is the one state worth nagging about.
     */
    const [pendingConfirmation, setPendingConfirmation] = useState<{
        conversationId: string;
        customerPhone: string;
        result: ConfirmDraftResult;
    } | null>(null);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);
    /**
     * A conversation switch held up by unsaved cart edits, waiting on the operator to decide. Carries
     * the navigation intent too, so approving it from the orders page still lands on the inbox.
     */
    const [pendingSwitch, setPendingSwitch] = useState<{
        conversation: Conversation;
        navigateHome: boolean;
    } | null>(null);
    /**
     * Offer to re-enable the AI on a taken-over conversation the operator just walked away from.
     * Non-blocking on purpose — the switch already happened; this dialog acts on the conversation
     * LEFT BEHIND. `customerWaiting` is a best-effort last-message check: it picks the description
     * and, on confirm, whether the agent takes a turn right away (takeover-era customer messages
     * are deliberately never re-batched, so without that turn the agent stays quiet until the
     * customer writes again); null = unknown, treated as "nothing owed".
     */
    const [aiResumePrompt, setAiResumePrompt] = useState<{
        conversation: Conversation;
        customerWaiting: boolean | null;
    } | null>(null);
    /**
     * Takeovers the operator already declined to end, keyed by conversation id → the
     * aiDisabledUntil they declined at. Sending another manual message extends aiDisabledUntil,
     * so the same conversation re-arms naturally; confirming records it too so a stale list row
     * can't re-prompt before the next poll.
     */
    const aiResumeDeclined = useRef(new Map<string, string>());
    /**
     * The stronger "don't remind me for this chat" choice: keyed on conversation id → epoch ms,
     * deliberately NOT on aiDisabledUntil — new operator messages must not re-arm it. Time-boxed
     * so a busy hour can't silence the offer for the rest of the shift.
     */
    const aiResumeSnoozedUntil = useRef(new Map<string, number>());
    const [hasOrdersCapability, setHasOrdersCapability] = useState(false);
    const [bankTransfersEnabled, setBankTransfersEnabled] = useState(false);
    const [brandName, setBrandName] = useState<string>();
    /** Read-only tenant identity (logo, colours) for the header — K6's `identity` block. */
    const [brandIdentity, setBrandIdentity] = useState<BrandIdentityDto>();
    const [locations, setLocations] = useState<LocationResponseDto[]>([]);
    const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
    const [brandId, setBrandId] = useState<string>();
    /**
     * Coupons rollout gate — `undefined` while the brand's POS providers are still loading, so the
     * route can hold instead of bouncing a deep link. See `couponsSurfaceEnabled`.
     */
    const [couponsEnabled, setCouponsEnabled] = useState<boolean>();
    /**
     * The brand's *enabled* POS provider types — `undefined` until they load, and left that way on
     * failure so the Discounts hub disables authoring without claiming the POS owns promotions.
     */
    const [connectedPosTypes, setConnectedPosTypes] = useState<string[]>();
    const conversationListRef = useRef<ConversationListRef>(null);

    // Agent status state (lifted from child components)
    const [phone, setPhone] = useState<string>();
    const [aiAgentEnabled, setAiAgentEnabled] = useState(false);
    const [aiAgentPaused, setAiAgentPaused] = useState(false);
    const [hasBrand, setHasBrand] = useState(false);

    useEffect(() => {
        // An aggregate session has no single brand: the session catalogue already carries
        // every brand's capabilities and line status, and both of these routes answer 423
        // there — so they are never called.
        if (isAggregate) return;
        // Fetch agent status
        brandClient
            .getStatus()
            .then((status) => {
                setPhone(status.phone);
                setAiAgentEnabled(status.aiAgentEnabled);
                setAiAgentPaused(status.aiAgentPaused);
                setHasBrand(status.hasBrand);
            })
            .catch((err) => {
                console.error('Failed to fetch brand status:', err);
            });

        // Fetch capabilities for orders tab
        brandClient
            .getBrand()
            .then((res) => {
                const capabilities = res.capabilities || [];
                setHasOrdersCapability(capabilities.includes('ORDERS'));
                // Transfers only exist for brands with bank details configured — the backend
                // rejects the whole /bank-transfers surface otherwise (same gate).
                setBankTransfersEnabled(!!res.transactionSettings?.bankTransferDetails);
                if (res.name) setBrandName(res.name);
                setBrandIdentity(res.identity);
                setLocations(res.locations || []);
                setBrandId(res.id);
            })
            .catch(() => {
                // No brand or error — capabilities stay false
            })
            .finally(() => {
                setCapabilitiesLoaded(true);
            });
    }, [isAggregate]);

    /** The one brand whose POS we may ask about: the session's own, or the single filtered brand. */
    const posBrandId = isAggregate ? activeBrand?.id : brandId;

    // Coupons are gated on the brand's POS integration for now (see coupons-client). Kept out of
    // the capabilities effect so the extra request can't delay the orders/transfers routes.
    useEffect(() => {
        if (!posBrandId) {
            // Aggregate filter spanning several brands — there is no single POS to ask about,
            // so drop whatever the last single-brand selection had learned.
            setConnectedPosTypes(undefined);
            setCouponsEnabled(undefined);
            return;
        }
        posProvidersClient
            .getProviders(posBrandId)
            .then((providers) => {
                const activeTypes = providers.filter((p) => p.enabled).map((p) => p.type);
                setConnectedPosTypes(activeTypes);
                setCouponsEnabled(couponsSurfaceEnabled(activeTypes));
            })
            .catch(() => {
                // Unknown reads as "no POS" so the landing below doesn't wait forever.
                setConnectedPosTypes([]);
                setCouponsEnabled(false);
            });
    }, [posBrandId]);

    /**
     * Aggregate capability derivation: a surface is offered when ANY brand currently in the
     * filter has it — narrowing the filter is what takes it away again.
     */
    const aggregateHasOrders = useMemo(
        () => selectedBrands.some((brand) => takesOrders(brand.capabilities.includes('ORDERS'), brand.channels)),
        [selectedBrands],
    );
    const aggregateHasTransfers = useMemo(
        () => selectedBrands.some((brand) => brand.bankTransfersEnabled),
        [selectedBrands],
    );
    /** The orders surfaces: ORDERS or a storefront — see `takesOrders`. */
    const brandTakesOrders = takesOrders(hasOrdersCapability, sessionAccess?.channels);
    const ordersCapability = isAggregate ? aggregateHasOrders : brandTakesOrders;
    const transfersEnabled = isAggregate ? aggregateHasTransfers : bankTransfersEnabled;
    const capabilitiesReady = isAggregate || capabilitiesLoaded;
    /**
     * Results (D18) is brand-wide numbers, so v1 shows it to the brand owner's own session only:
     * not to an aggregate (no single brand) and not to a branch login, whose `role` is `location`
     * — the same test the backend 403s on (`RESULTS_BRAND_ONLY`).
     */
    const showResults = capabilitiesReady && ordersCapability && !isAggregate && sessionAccess?.role === 'owner';
    const hasPosProvider = Boolean(connectedPosTypes?.length);
    /** The landing needs the POS answer too: a POS + WhatsApp brand starts on the inbox. */
    const landingReady = capabilitiesReady && (!posBrandId || connectedPosTypes !== undefined);

    /** The kill switch's brand, live — only ever fetched under a single-brand filter. */
    const { data: aggregateStatus, refetch: refetchAggregateStatus } = useBrandStatus(isAggregate ? activeBrand : null);

    /**
     * The master line of one catalogue brand. Aggregate sessions answer "can the AI actually
     * come back on this chat?" from here rather than from a single brand-wide status.
     */
    const lineStatusForBrand = useCallback(
        (id: string | undefined) => {
            const brand = id ? brandById.get(id) : undefined;
            const line = masterLineOf(brand);
            return {
                hasBrand: Boolean(brand),
                aiAgentEnabled: line?.aiAgentEnabled ?? false,
                aiAgentPaused: line?.aiAgentPaused ?? false,
            };
        },
        [brandById],
    );

    /**
     * 'pending' while either gate (ORDERS capability, POS rollout) is still resolving — the
     * Discounts hub leaves the Cupones tab out in that window instead of flashing it in and out.
     */
    const couponsAccess = useMemo<'pending' | 'allowed' | 'denied'>(() => {
        if (!capabilitiesReady) return 'pending';
        if (!ordersCapability) return 'denied';
        // Aggregate: the per-POS rollout gate is per brand and the hub spans several, so ORDERS
        // on any brand in the filter opens it; authoring is gated on a single brand instead.
        if (isAggregate) return 'allowed';
        if (couponsEnabled === undefined) return 'pending';
        return couponsEnabled ? 'allowed' : 'denied';
    }, [capabilitiesReady, ordersCapability, couponsEnabled, isAggregate]);

    const { pendingCount: pendingOrdersCount } = usePendingOrdersCount({
        // Neither orders surface needs the badge while the operator is looking at it.
        enabled: location.pathname !== '/board' && location.pathname !== '/orders' && ordersCapability,
    });

    const { handoffCount } = useHandoffCount({
        // Handoffs happen inside WhatsApp conversations: a session with no inbox (WhatsApp channel
        // off, or chats hidden from this sucursal) has no badge and no reason to poll for one.
        enabled: inboxVisible,
    });

    const handleToggleAiAgent = useCallback(
        async (paused: boolean, options?: { autoReplyMessage?: string; reactivateInMinutes?: number }) => {
            try {
                // In aggregate the kill switch only shows under a single-brand filter, and the
                // write must name that brand — the backend has no session brand to fall back on.
                const result = await brandClient.toggleAiAgent(paused, options, activeBrand?.id);
                if (isAggregate) {
                    void refetchAggregateStatus();
                } else {
                    setAiAgentPaused(result.aiAgentPaused);
                }
                toast.success(paused ? t('aiAgent.pausedToast') : t('aiAgent.activatedToast'));
            } catch (err) {
                toast.error(err instanceof Error ? err.message : t('aiAgent.toggleError'));
            }
        },
        [t, activeBrand?.id, isAggregate, refetchAggregateStatus],
    );

    const handleBrandCreatedOrUpdated = useCallback(() => {
        // Refresh status after brand creation/update (never reachable in aggregate: the
        // settings page is replaced by the config banner there).
        brandClient
            .getStatus()
            .then((status) => {
                setPhone(status.phone);
                setAiAgentEnabled(status.aiAgentEnabled);
                setAiAgentPaused(status.aiAgentPaused);
                setHasBrand(status.hasBrand);
            })
            .catch(() => {});
    }, []);

    const { draft, startContext, refetch: refetchDraft } = useConversationDraft({
        conversationId: selectedConversation?.id,
        paused: cartDirty,
        // Full cadence only while a cart is believed live or the panel is open; otherwise the poll
        // drops to a slow safety-net tick that merely guards against a stale list flag.
        active: Boolean(selectedConversation?.hasActiveOrder) || cartOpen,
        // What starting a cart would take (suggested branch, recent-order warning) costs the server
        // extra lookups, so only the open panel asks; with a live cart the server skips it anyway.
        includeStartContext: cartOpen,
    });

    // Selecting another conversation must not carry the cart (or a stale dirty flag) across.
    // `pendingConfirmation` is deliberately NOT cleared here — it is keyed by conversation and has to
    // survive the operator looking at something else before they get round to notifying the customer.
    useEffect(() => {
        setCartOpen(false);
        setCartDirty(false);
        setSummaryOpen(false);
    }, [selectedConversation?.id]);

    // Declared after the reset above, so on a switch it runs second and its open wins.
    useEffect(() => {
        if (!openCartFor || openCartFor !== selectedConversation?.id) return;
        setCartOpen(true);
        setOpenCartFor(null);
    }, [openCartFor, selectedConversation?.id]);

    /**
     * The selected conversation is a snapshot taken at click time; the list re-polls every 2s and is
     * the only thing that re-reads `hasActiveOrder`. Adopt just that flag from the fresh row — it
     * paces the cart poll above, and a stale `false` left the header stuck on "start order" for up to
     * 30s after the agent had already opened a draft. Identity is preserved when nothing changed, so
     * the routine 2s callback re-renders nothing.
     */
    const handleSelectedConversationRefreshed = useCallback((fresh: Conversation) => {
        setSelectedConversation((prev) =>
            prev && prev.id === fresh.id && prev.hasActiveOrder !== fresh.hasActiveOrder
                ? { ...prev, hasActiveOrder: fresh.hasActiveOrder }
                : prev,
        );
    }, []);

    // The flag just flipped on (the agent opened a draft): fetch the cart now instead of waiting out
    // the current poll tick, so the header button moves as soon as the list knows.
    useEffect(() => {
        if (selectedConversation?.hasActiveOrder && !draft && !cartDirty) void refetchDraft();
    }, [selectedConversation?.hasActiveOrder, draft, cartDirty, refetchDraft]);

    /**
     * After any panel action that changes conversation state: re-read the cart and the conversation
     * row, because a takeover or hand-back moves `aiDisabledUntil` and that is what decides whether
     * the panel is editable at all.
     */
    const handleCartChanged = async () => {
        // The conversation FIRST, then the cart. Both land either way, but a cart that appears before
        // its conversation's new pause does makes the panel flash its read-only "Tomar control" state
        // on the way in — which is exactly what an operator who just started the order did not do.
        if (selectedConversation?.phoneNumber) {
            await handleTemplateSent(selectedConversation.phoneNumber);
        }
        await refetchDraft();
    };

    const handleConfirmed = (result: ConfirmDraftResult) => {
        if (!selectedConversation?.id) return;
        setPendingConfirmation({
            conversationId: selectedConversation.id,
            customerPhone: selectedConversation.phoneNumber,
            result,
        });
        setSummaryOpen(true);
        setCartOpen(false);
    };

    const handleSendSummary = async (message: string) => {
        if (!pendingConfirmation) return;

        setSendingSummary(true);
        try {
            await conversationClient.messages.sendText({
                to: pendingConfirmation.customerPhone,
                body: message,
                role: 'assistant',
                conversationId: pendingConfirmation.conversationId,
            });
            // Only now: while the send is failing, the customer still has not been told, so the
            // reminder has to stay exactly where it was.
            setPendingConfirmation(null);
            setSummaryOpen(false);
            await handleTemplateSent(pendingConfirmation.customerPhone);
        } catch {
            toast.error(t('conversations:cart.errors.sendFailed'));
        } finally {
            setSendingSummary(false);
        }
    };

    /** The order the currently-selected conversation still owes its customer a message about. */
    const unsentConfirmation =
        pendingConfirmation && pendingConfirmation.conversationId === selectedConversation?.id
            ? pendingConfirmation
            : null;

    /** The operator holds the floor exactly while the conversation's AI pause is still in effect. */
    const hasTakenOver = Boolean(
        selectedConversation?.aiDisabledUntil && new Date(selectedConversation.aiDisabledUntil) > new Date(),
    );

    /**
     * The branches this session may act for — its own sucursal when it is scoped to one, the whole
     * brand otherwise. Shared by the orders filter and the cart's branch picker so the two can never
     * disagree about which branches this operator is allowed to place for.
     */
    const scopedLocations = useMemo(
        () =>
            sessionScope?.locationId
                ? locations.filter((location) => location.id === sessionScope.locationId)
                : locations,
        [locations, sessionScope?.locationId],
    );
    /**
     * In aggregate the cart belongs to the CONVERSATION's brand, not to the filter: the branch
     * picker and the ORDERS gate both come from that brand's catalogue entry.
     */
    const conversationBrand =
        isAggregate && selectedConversation?.brandId ? (brandById.get(selectedConversation.brandId) ?? null) : null;
    const cartBranches = useMemo(
        () =>
            isAggregate
                ? (conversationBrand?.locations ?? []).map((location) => ({ id: location.id, name: location.name }))
                : scopedLocations.map((location) => ({ id: location.id, name: location.name })),
        [isAggregate, conversationBrand, scopedLocations],
    );
    const canStartOrder = isAggregate
        ? Boolean(conversationBrand?.capabilities.includes('ORDERS'))
        : hasOrdersCapability;

    const handleTemplateSent = async (phoneNumber: string) => {
        const conversations = await conversationListRef.current?.refresh();
        if (conversations) {
            const conversation = conversations.find((conv) => conv.phoneNumber === phoneNumber);
            if (conversation) {
                setSelectedConversation(conversation);
            }
        }
    };

    /**
     * Leaving the conversation unmounts the cart panel and takes its staged edits with it, so a dirty
     * cart intercepts the switch rather than silently discarding work. Returns whether the caller may
     * proceed; when it may not, the confirmation below owns the decision.
     *
     * Only a genuine change of conversation counts — re-selecting the one already open is not a switch,
     * and the polled list re-selects the current conversation routinely.
     */
    const mayLeaveConversation = (next: Conversation, navigateHome = false): boolean => {
        if (!cartDirty || next.id === selectedConversation?.id) return true;
        setPendingSwitch({ conversation: next, navigateHome });
        return false;
    };

    /**
     * The operator is walking away from `previous` — if they still hold the floor there (active
     * takeover) and the brand's AI could actually answer, offer to hand the conversation back to
     * the agent. Offered, never automatic: the AI only comes back when the operator says so.
     */
    const maybeOfferAiResume = (previous: Conversation | undefined) => {
        if (!previous?.aiDisabledUntil) return;
        if (new Date(previous.aiDisabledUntil) <= new Date()) return;
        // Re-enabling per-conversation AI is an empty promise while the brand agent is off/paused.
        // Aggregate sessions read that per conversation's own brand, not from a single brand.
        const lineStatus = isAggregate
            ? lineStatusForBrand(previous.brandId)
            : { hasBrand, aiAgentEnabled, aiAgentPaused };
        if (!lineStatus.hasBrand || !lineStatus.aiAgentEnabled || lineStatus.aiAgentPaused) return;
        const snoozedUntil = aiResumeSnoozedUntil.current.get(previous.id);
        if (snoozedUntil !== undefined && snoozedUntil > Date.now()) return;
        if (aiResumeDeclined.current.get(previous.id) === previous.aiDisabledUntil) return;
        setAiResumePrompt({ conversation: previous, customerWaiting: null });
        // Best-effort: does the customer have the last word? Failure just means "unknown".
        void conversationClient.messages
            .listByConversation({ conversationId: previous.id, limit: 1 })
            .then((response) => {
                const newest = response.data[0];
                if (!newest) return;
                setAiResumePrompt((current) =>
                    current?.conversation.id === previous.id
                        ? { ...current, customerWaiting: newest.direction === 'incoming' }
                        : current,
                );
            })
            .catch(() => {});
    };

    const declineAiResume = () => {
        if (!aiResumePrompt) return;
        const { conversation } = aiResumePrompt;
        if (conversation.aiDisabledUntil) {
            aiResumeDeclined.current.set(conversation.id, conversation.aiDisabledUntil);
        }
        setAiResumePrompt(null);
    };

    /** "Ahora no" asks again once the operator re-engages; this stays quiet for a full hour. */
    const snoozeAiResume = () => {
        if (!aiResumePrompt) return;
        aiResumeSnoozedUntil.current.set(aiResumePrompt.conversation.id, Date.now() + AI_RESUME_SNOOZE_MS);
        setAiResumePrompt(null);
    };

    const confirmAiResume = () => {
        const prompt = aiResumePrompt;
        if (!prompt) return;
        setAiResumePrompt(null);
        const { conversation, customerWaiting } = prompt;
        if (conversation.aiDisabledUntil) {
            aiResumeDeclined.current.set(conversation.id, conversation.aiDisabledUntil);
        }
        const name = conversation.contactName || conversation.phoneNumber || conversation.id;
        void conversationClient.conversations
            .resumeAi(conversation.id, { requestAgentTurn: customerWaiting === true })
            .then((result) => {
                void conversationListRef.current?.refresh();
                toast.success(
                    result.agentTurnQueued
                        ? t('conversations:aiResumePrompt.resumedWithTurn', { name })
                        : t('conversations:aiResumePrompt.resumed', { name }),
                );
            })
            .catch(() => toast.error(t('conversations:aiResumePrompt.error')));
    };

    const handleSelectConversation = (conversation: Conversation) => {
        if (!mayLeaveConversation(conversation)) return;
        if (conversation.id !== selectedConversation?.id) maybeOfferAiResume(selectedConversation);
        setSelectedConversation(conversation);
        setSidebarOpen(false);
    };

    const handleViewConversationFromOrder = (conversationId: string, options?: ViewConversationOptions) => {
        const target: Conversation = { id: conversationId, phoneNumber: '' };
        if (!mayLeaveConversation(target, true)) return;
        if (target.id !== selectedConversation?.id) maybeOfferAiResume(selectedConversation);
        if (options?.openCart) setOpenCartFor(conversationId);
        setSelectedConversation(target);
        navigate('/conversations');
    };
    /** Undefined when the inbox is hidden: the order/transfer surfaces then drop their chat links. */
    const viewConversationFromOrder = inboxVisible ? handleViewConversationFromOrder : undefined;

    /** They chose to drop the staged edits. */
    const confirmPendingSwitch = () => {
        if (!pendingSwitch) return;
        // Cleared here as well as by the effect keyed on the conversation id: the guard reads this flag,
        // and leaving it set would make it fire again on the very switch it just approved.
        setCartDirty(false);
        // The guard only ever holds a GENUINE switch (same-id re-selects pass through it), so the
        // conversation being left behind gets its AI-resume offer here too.
        maybeOfferAiResume(selectedConversation);
        setSelectedConversation(pendingSwitch.conversation);
        setSidebarOpen(false);
        if (pendingSwitch.navigateHome) navigate('/conversations');
        setPendingSwitch(null);
    };

    // Deep links (orders, notifications) only carry the conversation id — hydrate the customer
    // name/phone (and AI/handoff state) from the API so the header doesn't render empty.
    useEffect(() => {
        const conversation = selectedConversation;
        if (!conversation?.id || conversation.phoneNumber) return;
        let cancelled = false;
        conversationClient.conversations
            .get(conversation.id)
            .then((res) => {
                if (cancelled) return;
                // A deep link (order, notification) can point at a chat whose brand the filter
                // currently hides — widen rather than open a conversation that no list shows.
                if (res.data.brandId) includeBrand(res.data.brandId);
                setSelectedConversation((prev) =>
                    prev?.id === conversation.id
                        ? {
                              id: conversation.id,
                              phoneNumber: res.data.customerPhone,
                              contactName: res.data.customerName,
                              aiDisabledUntil: res.data.aiDisabledUntil,
                              handoffUntil: res.data.handoffUntil,
                              brandId: res.data.brandId,
                              brandName: res.data.brandName,
                              locationName: res.data.locationName,
                              lineUnlinked: res.data.lineUnlinked,
                          }
                        : prev,
                );
            })
            .catch((err) => {
                console.error('Failed to load conversation details:', err);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedConversation, includeBrand]);

    // Pick up conversation from navigation state (e.g. from orders)
    useEffect(() => {
        const state = location.state as { conversationId?: string } | null;
        if (state?.conversationId && location.pathname === '/conversations') {
            setSelectedConversation({ id: state.conversationId, phoneNumber: '' });
            // Clear the state so it doesn't re-trigger on future navigations to the inbox
            navigate('/conversations', { replace: true, state: null });
        }
    }, [location, navigate]);

    // Pick up conversation from ?conversationId= query (e.g. from notification click).
    // Accepted on the home route too: `/?conversationId=` was the inbox's deep link before the
    // board took "/" over, and those links live on in bookmarks and in already-shown notifications.
    useEffect(() => {
        if (location.pathname !== '/conversations' && location.pathname !== '/') return;
        const params = new URLSearchParams(location.search);
        const conversationId = params.get('conversationId');
        if (conversationId) {
            setSelectedConversation({ id: conversationId, phoneNumber: '' });
            navigate('/conversations', { replace: true });
        }
    }, [location, navigate]);

    /**
     * The settings sections this session can see: channel-tagged (a section whose channel is off
     * does not exist), capability-gated (Discounts needs `takesOrders`) and mode-gated
     * (`settingsAccess.ts`). Derived here because the header, the `/settings` redirect and the
     * section routes all need the same answer.
     */
    const settingsSections = useMemo(
        () =>
            isAggregate
                ? []
                : visibleSettingsSections(
                      getSessionMode(sessionScope),
                      sessionAccess?.channels,
                      // Discounts hangs off `takesOrders`; `undefined` until the brand answers, so a
                      // deep link into the section holds instead of bouncing.
                      capabilitiesLoaded ? brandTakesOrders : undefined,
                  ),
        [isAggregate, sessionScope, sessionAccess, capabilitiesLoaded, brandTakesOrders],
    );

    const settingsRouteProps = {
        sections: settingsSections,
        sessionScope,
        isAggregate,
        onBack: () => navigate('/'),
        onBrandChanged: handleBrandCreatedOrUpdated,
        onNavigateSection: (path: string) => navigate(`/settings/${path}`),
        readOnlyBanner: sessionScope?.readOnly
            ? sessionScope.hasOwnLine
                ? t('scopeBanner.overridable')
                : t('scopeBanner.readOnly')
            : undefined,
    };

    // Leaving the inbox for another page is walking away from the open chat too. Latest-ref
    // pattern so the effect fires only on an actual route change, never on selection changes.
    const inboxExitRef = useRef({ pathname: location.pathname, offer: () => {} });
    inboxExitRef.current.offer = () => maybeOfferAiResume(selectedConversation);
    useEffect(() => {
        const cameFromInbox = inboxExitRef.current.pathname === '/conversations';
        inboxExitRef.current.pathname = location.pathname;
        if (cameFromInbox && location.pathname !== '/conversations') inboxExitRef.current.offer();
    }, [location.pathname]);

    return (
        <AppLayout
            hasOrdersCapability={ordersCapability}
            showResults={showResults}
            showWhatsappChrome={whatsappChrome}
            showInbox={inboxVisible}
            hasWhatsappLine={whatsappLine}
            brandName={isAggregate ? undefined : brandName}
            brandIdentity={isAggregate ? undefined : brandIdentity}
            phone={isAggregate ? aggregateStatus?.phone : phone}
            /* The AI pill is a property of the tenant's WhatsApp channel now, not of the line:
               it shows only when `channels.whatsapp.aiAgentEnabled` is on. */
            aiAgentEnabled={
                isAggregate
                    ? (aggregateStatus?.aiAgentEnabled ?? false)
                    : (sessionAccess?.channels.whatsapp.aiAgentEnabled ?? false)
            }
            aiAgentPaused={isAggregate ? (aggregateStatus?.aiAgentPaused ?? false) : aiAgentPaused}
            hasBrand={isAggregate ? Boolean(activeBrand) : hasBrand}
            onToggleAiAgent={handleToggleAiAgent}
            pendingOrdersCount={pendingOrdersCount}
            handoffCount={handoffCount}
        >
            <Suspense fallback={<PageFallback />}>
                <Routes>
                    {/*
                        "/" is a LANDING decision, not a surface. The tenant's commerce mode
                        (§6.1, C5/C6) picks which surface the operator's day starts on and
                        `homePath` owns that rule: mode 2 (AI on, or web on, or the external agent
                        on) starts on the orders board — the work queue first, with the inbox one
                        click from any card that has a chat; mode 1 (a WhatsApp line with no AI)
                        starts on the inbox, and so does any brand that takes no orders or has
                        a POS connected and a WhatsApp channel (the POS works the orders).

                        It does NOT decide whether the board EXISTS — that is `/board` below, gated
                        on `takesOrders` alone (ORDERS, or a storefront). A manual-WhatsApp brand
                        takes its orders by hand from the chat ("Nuevo pedido") and needs the board
                        to work them; mode 1 makes the board secondary, never absent. Keeping the
                        board on "/" is what
                        used to make the header's board button bounce straight back to the inbox.
                    */}
                    <Route
                        path="/"
                        element={
                            !landingReady ? null : homePath(sessionAccess, ordersCapability, hasPosProvider) === '/board' ? (
                                <Navigate to="/board" replace />
                            ) : inboxVisible ? (
                                <Navigate to="/conversations" replace />
                            ) : (
                                // Neither surface exists (no orders, no WhatsApp channel in scope):
                                // the inbox would bounce straight back here, so land on a page.
                                <NoSurfaceEmptyState />
                            )
                        }
                    />
                    <Route
                        path="/board"
                        element={
                            !capabilitiesReady ? null : ordersCapability ? (
                                <OrdersBoard
                                    bankTransfersEnabled={transfersEnabled}
                                    /* POS brands: the board drops the columns the POS manages and reads
                                       "sent to POS" as completed. */
                                    hasPosProvider={hasPosProvider}
                                    locations={scopedLocations}
                                    onViewConversation={viewConversationFromOrder}
                                />
                            ) : (
                                // No board for this brand: "/" knows where it does belong.
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/conversations"
                        element={
                            // A web-only tenant has no inbox: nothing ever arrives in it, and
                            // the list would fire scope-based reads for a channel that is off.
                            // (In aggregate `whatsappChrome` already spans the brands in the filter.)
                            // Nor does a sucursal whose brand hides its chats from locations.
                            !inboxVisible ? (
                                <Navigate to="/" replace />
                            ) : (
                            <>
                                <ConversationList
                                    ref={conversationListRef}
                                    onSelectConversation={handleSelectConversation}
                                    selectedConversationId={selectedConversation?.id}
                                    onSelectedConversationRefreshed={handleSelectedConversationRefreshed}
                                    sidebarOpen={sidebarOpen}
                                    onCloseSidebar={() => setSidebarOpen(false)}
                                    handoffCount={handoffCount}
                                    desktopCollapsed={cartOpen && Boolean(selectedConversation?.id)}
                                />
                                <MessageView
                                    key={selectedConversation?.id}
                                    conversationId={selectedConversation?.id}
                                    phoneNumber={selectedConversation?.phoneNumber}
                                    contactName={selectedConversation?.contactName}
                                    aiDisabledUntil={selectedConversation?.aiDisabledUntil}
                                    handoffUntil={selectedConversation?.handoffUntil}
                                    onTemplateSent={handleTemplateSent}
                                    onConversationStateChanged={handleTemplateSent}
                                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                                    brandId={selectedConversation?.brandId}
                                    brandName={selectedConversation?.brandName}
                                    locationName={selectedConversation?.locationName}
                                    lineUnlinked={selectedConversation?.lineUnlinked}
                                    draftItemCount={draft ? draft.items.length : undefined}
                                    canStartOrder={canStartOrder}
                                    cartOpen={cartOpen}
                                    onToggleCart={() => setCartOpen((open) => !open)}
                                    hasUnsentConfirmation={Boolean(unsentConfirmation)}
                                    onOpenConfirmationSummary={() => setSummaryOpen(true)}
                                    onDismissConfirmation={() => setPendingConfirmation(null)}
                                />
                                {cartOpen &&
                                    selectedConversation?.id &&
                                    (draft ? (
                                        <CartPanel
                                            key={draft.orderId}
                                            cart={draft}
                                            conversationId={selectedConversation.id}
                                            brandName={selectedConversation.brandName}
                                            branches={cartBranches}
                                            hasTakenOver={hasTakenOver}
                                            onDirtyChange={setCartDirty}
                                            onClose={() => setCartOpen(false)}
                                            onCartChanged={handleCartChanged}
                                            onConfirmed={handleConfirmed}
                                        />
                                    ) : (
                                        // Same slot: the operator opens "the cart" and finds either the
                                        // order in progress or the way to begin one. Gated on the
                                        // capability, like the button that opens it.
                                        canStartOrder && (
                                            <CartStartPanel
                                                conversationId={selectedConversation.id}
                                                brandName={selectedConversation.brandName}
                                                branches={cartBranches}
                                                startContext={startContext}
                                                hasTakenOver={hasTakenOver}
                                                onClose={() => setCartOpen(false)}
                                                onStarted={handleCartChanged}
                                            />
                                        )
                                    ))}
                            </>
                            )
                        }
                    />
                    <Route
                        path="/orders"
                        element={
                            !capabilitiesReady ? null : ordersCapability ? (
                                <OrdersTab
                                    brandPhone={isAggregate ? undefined : phone}
                                    bankTransfersEnabled={transfersEnabled}
                                    locations={scopedLocations}
                                    onViewConversation={viewConversationFromOrder}
                                />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/bank-transfers"
                        element={
                            !capabilitiesReady ? null : ordersCapability && transfersEnabled ? (
                                <BankTransfersTab
                                    locations={scopedLocations}
                                    onViewConversation={viewConversationFromOrder}
                                />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/templates"
                        element={
                            // Templates are a per-line resource (like flows): own-line
                            // sessions manage their line's templates; shared-line
                            // sessions ride the master's line and only read them. An aggregate
                            // session spans many lines, so there is nothing to manage here.
                            // No WhatsApp channel, or no line yet: the surface does not exist.
                            !isAggregate && (!whatsappChrome || !whatsappLine) ? (
                                <Navigate to="/" replace />
                            ) : isAggregate ? (
                                <AggregateConfigBanner />
                            ) : sessionScope && !sessionScope.hasOwnLine ? (
                                <div className="flex-1 flex flex-col min-h-0">
                                    <ScopeBanner message={t('scopeBanner.readOnly')} />
                                    <TemplateManagementPage readOnly />
                                </div>
                            ) : (
                                <TemplateManagementPage />
                            )
                        }
                    />
                    <Route
                        path="/discounts"
                        element={
                            // The hub itself rides `takesOrders` alone: a POS brand that manages its own
                            // promotions still has to see them here, read-only. The coupons gate
                            // (ORDERS + the per-POS rollout allowlist) applies one level down, to
                            // the Cupones tab, which is why it is passed in rather than checked here.
                            !capabilitiesReady ? null : ordersCapability ? (
                                <DiscountsPage couponsAccess={couponsAccess} connectedPosTypes={connectedPosTypes} />
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="/results"
                        element={
                            !capabilitiesReady ? null : showResults ? <ResultsPage /> : <Navigate to="/" replace />
                        }
                    />
                    {/* Coupons moved into the hub — keep old links, notifications and bookmarks alive. */}
                    <Route path="/coupons" element={<Navigate to="/discounts" replace />} />
                    <Route
                        path="/playground"
                        element={
                            !isAggregate && (!whatsappChrome || !whatsappLine) ? (
                                <Navigate to="/" replace />
                            ) : isAggregate ? (
                                <AggregateConfigBanner />
                            ) : (
                                <PlaygroundPage />
                            )
                        }
                    />
                    {/*
                        Settings are routes (§6.2), one per section, so a deep link, a reload and
                        the dashboard's `returnTo` (K8) all land on the same place. One splat route
                        rather than `:section` plus an entry for `whatsapp/flows`: the section path
                        can be two segments, and a single route keeps the page MOUNTED across
                        section changes — separate routes would remount it and refetch the brand on
                        every click in the nav.
                    */}
                    <Route path="/settings" element={<SettingsRedirect sections={settingsSections} />} />
                    <Route path="/settings/*" element={<SettingsSectionRoute {...settingsRouteProps} />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Suspense>

            {/*
                Outside the routes on purpose: the order is already placed, and the operator must be
                able to reach its tracking and payment links whatever they navigate to next. Closing
                the dialog does not discard it — the strip in the conversation brings it back.
            */}
            <ConfirmSummaryDialog
                open={summaryOpen && unsentConfirmation !== null}
                result={unsentConfirmation?.result ?? null}
                submitting={sendingSummary}
                onClose={() => setSummaryOpen(false)}
                onSend={(message) => void handleSendSummary(message)}
            />

            {/*
                Staged cart edits about to be lost. Cancel is the default action — there is no "save
                and switch" because the working copy lives in the panel, and a save can be refused
                (out of zone, a repriced line) in ways that need the operator to be looking at it.
            */}
            <AlertDialog open={pendingSwitch !== null} onOpenChange={(open) => !open && setPendingSwitch(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('conversations:cart.unsavedSwitch.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('conversations:cart.unsavedSwitch.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('conversations:cart.unsavedSwitch.stay')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmPendingSwitch}>
                            {t('conversations:cart.unsavedSwitch.discard')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/*
                The chat just left behind is still in manual mode. Offered, never automatic: the AI
                only comes back when the operator says so — and when the customer's last messages
                went unanswered, confirming also has the agent answer them right away
                (requestAgentTurn), since takeover-era messages are never re-batched on their own.
            */}
            <AlertDialog open={aiResumePrompt !== null} onOpenChange={(open) => !open && declineAiResume()}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('conversations:aiResumePrompt.title', {
                                name:
                                    aiResumePrompt?.conversation.contactName ||
                                    aiResumePrompt?.conversation.phoneNumber ||
                                    '',
                            })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {aiResumePrompt?.customerWaiting
                                ? t('conversations:aiResumePrompt.descriptionWaiting')
                                : t('conversations:aiResumePrompt.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('conversations:aiResumePrompt.dismiss')}</AlertDialogCancel>
                        {/* Not an AlertDialogCancel: closing through Radix would run declineAiResume
                            (the aiDisabledUntil-keyed dismissal) instead of the 1-hour snooze. */}
                        <Button variant="outline" onClick={snoozeAiResume}>
                            {t('conversations:aiResumePrompt.snooze')}
                        </Button>
                        <AlertDialogAction onClick={confirmAiResume}>
                            {t('conversations:aiResumePrompt.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppLayout>
    );
}

interface SettingsRouteProps {
    sections: SectionVisibility[];
    sessionScope: SessionScope | null;
    isAggregate: boolean;
    onBack: () => void;
    onBrandChanged: () => void;
    onNavigateSection: (path: string) => void;
    /** Scoped sessions get the "managed at master level" notice above the sections. */
    readOnlyBanner: string | undefined;
}

/**
 * `/settings` has no content of its own: it forwards to the session's default section — the
 * WhatsApp agent surface when the tenant has it, otherwise the first one in nav order. A tenant
 * whose sections are all hidden (an aggregate session) has no settings surface at all, and the
 * route says so rather than rendering an empty shell.
 */
function SettingsRedirect({ sections }: { sections: SectionVisibility[] }) {
    const fallback = defaultSettingsSection(sections);
    if (!fallback) return <AggregateConfigBanner />;
    return <Navigate to={`/settings/${fallback.path}`} replace />;
}

/**
 * One settings section. The URL is the state: an unknown or currently-invisible section (a
 * bookmark from before a channel was turned off, a hand-typed path) forwards to the first
 * visible one instead of erroring.
 */
function SettingsSectionRoute({
    sections,
    sessionScope,
    isAggregate,
    onBack,
    onBrandChanged,
    onNavigateSection,
    readOnlyBanner,
}: SettingsRouteProps) {
    // The splat: 'general', or 'whatsapp/flows'.
    const path = useParams()['*'];

    const fallback = defaultSettingsSection(sections);
    if (isAggregate || !fallback) return <AggregateConfigBanner />;

    const section = sections.find((candidate) => candidate.path === path);
    if (!section) return <Navigate to={`/settings/${fallback.path}`} replace />;

    return (
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {readOnlyBanner && <ScopeBanner message={readOnlyBanner} />}
            <ErrorBoundary label="Se rompió la configuración">
                <BrandManagementPage
                    onBack={onBack}
                    onBrandChanged={onBrandChanged}
                    sessionScope={sessionScope}
                    section={section}
                    sections={sections}
                    onNavigateSection={onNavigateSection}
                />
            </ErrorBoundary>
        </div>
    );
}
